import React, { useState } from "react";

import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import ActionsMenu from "../Submissions/ActionsMenu";

import { convertUtcToLocal } from "../../../Common/utils/dateUtils";
import { buildCreateSampleSubmissionPath, PRODUCT_ENVIRONMENT_CATEGORY } from "../../utils";

import { getDraftSubmissionsInfo } from "../../../../actions/sampleSubmission";

import { ReactComponent as ProductEnvironmentIcon } from "../../../../assets/icons/Product-Environment.svg";
import { ReactComponent as EnvironmentIcon } from "../../../../assets/images/feature-icons/EnvironmentOutlined.svg";
import { ReactComponent as ProductIcon } from "../../../../assets/images/feature-icons/ProductOutlined.svg";

import "./DraftSubmissionCard.css";

export default function DraftSubmissionCard({
  displayIndex,
  originalIndex,
  submission,
  setDraftEditing,
  setShowSubmissionForm,
  handleDeleteDraft,
  setNewAbortController,
  clearAbortController,
}) {
  const [loading, setLoading] = useState(false);
  const history = useHistory();

  const getDraftDateIso = (s) => {
    if (s.type === "scheduled") {
      return s.schedule_date;
    }
    if (s.type === "non_scheduled") {
      return s.submission_date;
    }
    return s.submission_date;
  };

  const handleClickDraft = async () => {
    setLoading(true);
    const newAbortController = setNewAbortController();
    const {
      submission_id,
      submission_name,
      sample_category,
    } = submission;
    const listDateIso = getDraftDateIso(submission);
    const { success, message, result } = await getDraftSubmissionsInfo(submission.submission_id, newAbortController.signal);
    if (newAbortController.signal.aborted) {
      setLoading(false);
      return;
    }
    if (success) {
      setDraftEditing({
        submission_id,
        submission_name,
        submission_date: listDateIso ? convertUtcToLocal(listDateIso) : undefined,
        ...result,
        sample_category,
      });
      setShowSubmissionForm(true);
      history.push(buildCreateSampleSubmissionPath(sample_category));
    } else {
      toast.error(message);
    }
    clearAbortController();
    setLoading(false);
  };

  const displayDateIso = getDraftDateIso(submission);
  const dateMoment = displayDateIso
    ? moment(convertUtcToLocal(displayDateIso))
    : null;
  let dateLabel = "—";
  if (dateMoment && dateMoment.isValid()) {
    dateLabel = submission.type === "scheduled"
      ? dateMoment.format("DD MMM YYYY")
      : dateMoment.format("DD MMM YYYY · hh:mm A");
  }

  const n = submission.num_samples ?? 0;
  const samplesLabel = `${n} ${n === 1 ? "Sample" : "Samples"} added`;

  const titleText = `${displayIndex}. ${submission.submission_name}`;

  const isProduct = submission.sample_category === "product";
  const isProductAndEnvironment = submission.sample_category === PRODUCT_ENVIRONMENT_CATEGORY;
  const CategoryIcon = isProductAndEnvironment
    ? ProductEnvironmentIcon
    : (isProduct ? ProductIcon : EnvironmentIcon);

  return (
    <li className="draft-submission-card">
      <div className="draft-submission-card__top">
        <button
          type="button"
          className="draft-submission-card__title-btn"
          onClick={() => (loading ? null : handleClickDraft())}
          disabled={loading}
        >
          {loading ? (
            <span className="draft-submission-card__title-text draft-submission-card__title-text--loading">
              {titleText}
              <span className="draft-submission__Loading">
                <Spin indicator={<LoadingOutlined className="draft-submission__Checkbox__Spin" spin />} />
              </span>
            </span>
          ) : (
            <span className="draft-submission-card__title-text">{titleText}</span>
          )}
        </button>
        <div
          className="draft-submission-card__actions"
          onClick={(e) => e.stopPropagation()}
        >
          <ActionsMenu
            handleEdit={handleClickDraft}
            handleDelete={() => handleDeleteDraft(originalIndex)}
            disabled={loading}
            disabledTooltipText="Loading draft…"
          />
        </div>
      </div>
      <div className="draft-submission-card__tags">
        <div className="draft-submission-card__tags-row">
          <span className="draft-submission-card__tag">
            {dateLabel}
          </span>
          <span className="draft-submission-card__tag draft-submission-card__tag--samples">
            <CategoryIcon className="draft-submission-card__tag-icon" aria-hidden />
            {samplesLabel}
          </span>
        </div>
        {submission.submitted_by ? (
          <div className="draft-submission-card__tags-row">
            <span
              className="draft-submission-card__tag draft-submission-card__tag--submitted-by"
              title={submission.submitted_by}
            >
              {submission.submitted_by}
            </span>
          </div>
        ) : null}
      </div>
    </li>
  );
}
